// src/catalogue.js
//
// The product catalogue, and how an order's lines are read.
//
// Every product is one document in the `products` collection — its names,
// price, stock, photos and tax treatment — and every page reads it from
// there, so a product added in the dashboard appears on the storefront, in
// the cart, at checkout, in the emails, on the invoices and throughout the
// dashboard with no code changing.
//
// An order keeps its own copy of what was bought, as `lines`:
//
//   [{ productId, name, qty, price, tax }]
//
// copied off the product when the order is placed, so an order goes on
// describing the sale it was however the product changes afterwards. Orders
// from before `lines` existed stored one field per product (qtyA, priceA, ...)
// and are read through LEGACY_PRODUCTS until they are moved.
//
// functions/catalogue.js is the server's copy of this file. The Cloud
// Functions package is deployed on its own and cannot import out of src/, so
// the two are kept in step by hand. Change one, change the other.

/**
 * The six products the shop sold before products could be added in the
 * dashboard, exactly as the code that sold them described them. History, not
 * configuration: it is read only to understand data stored in the old shape.
 * See functions/catalogue.js for what each field is.
 */
export const LEGACY_PRODUCTS = Object.freeze(
  [
    {
      id: '1',
      doc: 'teffGradeA',
      qtyField: 'qtyA',
      priceField: 'priceA',
      name: 'Teff Grade A Bales',
      itemName: 'Teff Grade A Bale',
      shortName: 'Teff A',
      description: 'Premium quality Teff Grade A hay bale.',
      tax: 'zero',
    },
    {
      id: '2',
      doc: 'teffGradeB',
      qtyField: 'qtyB',
      priceField: 'priceB',
      name: 'Teff Grade B Bales',
      itemName: 'Teff Grade B Bale',
      shortName: 'Teff B',
      description: 'Standard quality Teff Grade B hay bale.',
      tax: 'zero',
    },
    {
      id: '3',
      doc: 'lucerne',
      qtyField: 'qtyLucerne',
      priceField: 'priceLucerne',
      name: 'Lucerne Hay Bales',
      itemName: 'Lucerne Bale',
      shortName: 'Lucerne',
      description: 'Nutritious lucerne hay bale.',
      tax: 'zero',
    },
    {
      id: '4',
      doc: 'eragrostis',
      qtyField: 'qtyEragrostis',
      priceField: 'priceEragrostis',
      name: 'Eragrostis Hay Bales',
      itemName: 'Eragrostis Bale',
      shortName: 'Eragrostis',
      description: 'Natural eragrostis hay bale.',
      tax: 'zero',
    },
    {
      id: '5',
      doc: 'oats',
      qtyField: 'qtyOats',
      priceField: 'priceOats',
      name: 'Oats Hay Bales',
      itemName: 'Oats Bale',
      shortName: 'Oats',
      description: 'Healthy oats hay bale.',
      tax: 'zero',
    },
    {
      id: '6',
      doc: 'shavings',
      qtyField: 'qtyShavings',
      priceField: 'priceShavings',
      name: 'Shavings',
      itemName: 'Shavings',
      shortName: 'Shavings',
      description: 'Weight 26-28kg/bag (Same supplier as 2024)',
      tax: 'standard',
    },
  ].map((product) => Object.freeze(product))
);

/**
 * The VAT treatments a product may carry, as the dashboard offers them. What
 * each one means on an invoice is decided in functions/company.js.
 */
export const TAX_TREATMENTS = [
  { value: 'zero', label: 'Zero-rated (0%)', hint: 'Animal feed, such as hay and lucerne' },
  { value: 'standard', label: 'Standard-rated (15%)', hint: 'Most goods, such as bedding' },
  { value: 'exempt', label: 'Exempt', hint: 'Not a taxable supply at all' },
];

/** What a product is taxed as when nothing says otherwise. */
export const DEFAULT_TAX = 'standard';

const TAX_VALUES = TAX_TREATMENTS.map((treatment) => treatment.value);

const PRODUCT_ID = /^[A-Za-z0-9]{1,40}$/;

/** A product id as a document path and a map key can safely carry. */
export const isProductId = (value) => typeof value === 'string' && PRODUCT_ID.test(value);

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** A tax setting company.js understands; anything else is the standard rate. */
export function normaliseTax(value) {
  if (TAX_VALUES.includes(value)) return value;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1) return value;
  return DEFAULT_TAX;
}

/** One of the product, as an order, an email or an invoice names it. */
export const itemNameOf = (product) =>
  String(product?.itemName || product?.name || '').trim();

/** The dashboard's label for it, where a full name will not fit. */
export const shortNameOf = (product) =>
  String(product?.shortName || '').trim() || itemNameOf(product);

/** Display order: the order set in the dashboard, then by name. */
export function sortProducts(products) {
  const position = (product) => {
    const order = Number(product?.sortOrder);
    return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
  };
  return [...(products || [])].sort(
    (a, b) =>
      position(a) - position(b) ||
      String(a.name || '').localeCompare(String(b.name || '')) ||
      String(a.id).localeCompare(String(b.id))
  );
}

/** One stored line, in the shape every reader can rely on. */
export function normaliseLine(line) {
  return {
    productId: String(line?.productId ?? ''),
    name: String(line?.name || '').trim(),
    qty: num(line?.qty),
    price: num(line?.price),
    tax: normaliseTax(line?.tax),
  };
}

/** Lines for an order stored in the old one-field-per-product shape. */
export function linesFromLegacyFields(order) {
  return LEGACY_PRODUCTS.map((product) => ({
    productId: product.id,
    name: product.itemName,
    qty: num(order?.[product.qtyField]),
    price: num(order?.[product.priceField]),
    tax: product.tax,
  })).filter((line) => line.qty > 0);
}

/** Whether an order is still in the old shape, waiting to be moved. */
export const hasLegacyShape = (order) => !Array.isArray(order?.lines);

/**
 * What an order bought: `[{ productId, name, qty, price, tax }]`, one entry
 * per product with a quantity above zero, in the order they were sold. Its own
 * `lines` when it has them, otherwise read off the old fields.
 */
export function orderLines(order) {
  if (hasLegacyShape(order)) return linesFromLegacyFields(order);
  return order.lines.map(normaliseLine).filter((line) => line.productId !== '' && line.qty > 0);
}

/**
 * Why stock moved, as the dashboard says it. Every change to a product's
 * `available` or `reserved` is logged in `stockMovements` with one of these.
 */
export const STOCK_REASON_LABELS = {
  opening_balance: 'Opening balance',
  legacy_sync: 'Brought across from the old stock record',
  checkout: 'Paid online',
  manual_order: 'Manual order',
  order_edited: 'Order edited',
  order_deleted: 'Order deleted',
  fulfilled: 'Handed out',
  adjustment: 'Adjusted by hand',
  stock_count: 'Set from a stock count',
};

/**
 * One stock movement, without its timestamp: the writer adds `at` as a server
 * timestamp. `available` and `reserved` are the change, not the new figure.
 */
export function stockMovement({
  productId,
  productName = '',
  available = 0,
  reserved = 0,
  reason,
  orderId = null,
  orderNum = null,
  note = '',
  by = 'system',
}) {
  return {
    productId: String(productId),
    productName: String(productName || ''),
    available: num(available),
    reserved: num(reserved),
    reason: reason in STOCK_REASON_LABELS ? reason : 'adjustment',
    orderId: orderId || null,
    orderNum: orderNum || null,
    note: String(note || ''),
    by: String(by || 'system'),
  };
}

//
// ——— WHAT AN ORDER DOES TO STOCK ——————————————————————————————————————————
//
// Placing an order takes its units out of `available` and holds them in
// `reserved`; handing it out releases them from `reserved`; deleting it hands
// them back. Rather than encode each of those steps separately — which is how
// stock drifts — every change to an order is worked out as the difference
// between what the order holds before and what it holds after.

/**
 * Whether an order's units have been taken out of stock. A storefront order
 * records it as `stock_applied` once its payment is confirmed. An order
 * recorded in the dashboard before that flag existed was taken from stock the
 * moment it was recorded paid. A deleted order has handed its units back.
 */
export const stockApplied = (order) =>
  order?.deleted !== true &&
  (order?.stock_applied === true || (order?.stock_applied === undefined && order?.paid === true));

/**
 * What an order is holding, per product: `{ productId: { available, reserved } }`,
 * as changes from the stock it would leave behind if it did not exist. An
 * order still to go out holds its units in reserved; one handed out has taken
 * them for good; one that never took stock holds nothing.
 */
export function stockHeld(order, lines = orderLines(order)) {
  const held = {};
  if (!stockApplied(order)) return held;
  lines.forEach((line) => {
    const entry = (held[line.productId] = held[line.productId] || { available: 0, reserved: 0 });
    entry.available -= line.qty;
    if (!order.fulfilled) entry.reserved += line.qty;
  });
  return held;
}

/**
 * The stock moves that take an order from one state to another, as
 * `[{ productId, available, reserved }]`, leaving out products that do not
 * move. Pass null for "no order" — before it is created, after it is deleted.
 */
export function stockChange(before, after) {
  const from = before ? stockHeld(before) : {};
  const to = after ? stockHeld(after) : {};
  return [...new Set([...Object.keys(from), ...Object.keys(to)])]
    .map((productId) => ({
      productId,
      available: (to[productId]?.available || 0) - (from[productId]?.available || 0),
      reserved: (to[productId]?.reserved || 0) - (from[productId]?.reserved || 0),
    }))
    .filter((move) => move.available !== 0 || move.reserved !== 0);
}
